import React from 'react';
import { FiHash } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const IntegersLesson = () => {
  const sections = [
    {
      id: 'introduction',
      title: 'What Are Integers?',
      content: {
        introduction:
          'Integers are whole numbers that can be positive, negative, or zero. They form the fundamental building blocks of number theory.',

        concepts: [
          {
            title: 'Integer Definition and Set',
            explanation:
              'Integers include all whole numbers: ..., -3, -2, -1, 0, 1, 2, 3, ... They do NOT include fractions, decimals, or irrational numbers.',
            example: {
              problem:
                'Which of the following are integers: 5, -7, 0, 3.5, §2/3§, §sqrt(9)§?',
              solution: [
                'Check each number for whole number property:',
                '5: whole number ✓',
                '-7: whole number ✓',
                '0: whole number ✓',
                '3.5: decimal, not whole ✗',
                '§2/3§: fraction, not whole ✗',
                '§sqrt(9) = 3§: whole number ✓',
                'Answer: 5, -7, 0, and §sqrt(9)§ are integers',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'even-odd',
      title: 'Even and Odd Integers',
      content: {
        introduction:
          'Understanding even and odd properties is crucial for solving many GRE problems involving integer relationships.',

        concepts: [
          {
            title: 'Even Integer Properties',
            explanation:
              'Even integers are divisible by 2 and can be written as §2k§ where §k§ is any integer. Key operations: Even ± Even = Even, Even × Any Integer = Even.',
            example: {
              problem:
                'If n is an even integer, what can we say about §n^2 + 2§?',
              solution: [
                'Since n is even, §n^2§ is also even (even × even = even)',
                'Adding 2 (even) to §n^2§ (even): even + even = even',
                'Therefore, §n^2 + 2§ is even',
                'Answer: §n^2 + 2§ is even',
              ],
            },
          },
          {
            title: 'Odd Integer Properties',
            explanation:
              'Odd integers are not divisible by 2 and can be written as §2k + 1§. Key operations: Odd ± Odd = Even, Odd × Odd = Odd.',
            example: {
              problem:
                'The sum of 5 consecutive integers starting from an odd number n is always:',
              solution: [
                'The 5 consecutive integers are: §n, n+1, n+2, n+3, n+4§',
                'Sum = §n + (n+1) + (n+2) + (n+3) + (n+4) = 5n + 10§',
                'Factor out 5: §5(n + 2)§',
                'Since this is always divisible by 5, the sum is always divisible by 5',
                'Answer: Divisible by 5',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'prime-composite',
      title: 'Prime and Composite Numbers',
      content: {
        introduction:
          'Prime and composite numbers form the foundation of number theory and appear frequently in GRE quantitative reasoning.',

        concepts: [
          {
            title: 'Prime Numbers',
            explanation:
              'A prime number is a positive integer greater than 1 that has exactly two factors: 1 and itself. The first 10 primes are: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29.',
            example: {
              problem: 'How many prime numbers are there between 20 and 30?',
              solution: [
                'Check each number from 21 to 29:',
                '§21 = 3 × 7§ (composite)',
                '§22 = 2 × 11§ (composite)',
                '23: only factors are 1 and 23 (prime)',
                '§24 = 2^3 × 3§ (composite)',
                '§25 = 5^2§ (composite)',
                '§26 = 2 × 13§ (composite)',
                '§27 = 3^3§ (composite)',
                '§28 = 2^2 × 7§ (composite)',
                '29: only factors are 1 and 29 (prime)',
                'Answer: 2 prime numbers (23 and 29)',
              ],
            },
          },
          {
            title: 'Composite Numbers',
            explanation:
              'A composite number is a positive integer greater than 1 that has more than two factors. Every composite number can be expressed as a product of primes.',
            example: {
              problem: 'Express 60 as a product of prime factors',
              solution: [
                'Start dividing by smallest primes:',
                '§60 ÷ 2 = 30§',
                '§30 ÷ 2 = 15§',
                '§15 ÷ 3 = 5§',
                '§5 ÷ 5 = 1§',
                'Answer: §60 = 2^2 × 3 × 5§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'factors-multiples',
      title: 'Factors and Multiples',
      content: {
        introduction:
          'Factors and multiples are essential concepts for solving problems involving divisibility, GCD, and LCM.',

        concepts: [
          {
            title: 'Finding All Factors',
            prerequisiteNote:
              'You should be comfortable multiplying pairs of positive integers.',
            vocabulary: ['factor', 'multiple', 'factor pair', 'square root'],
            explanation:
              'To find all factors of a number, list factor pairs systematically. Start from 1 and stop when you reach §sqrt(n)§.',
            microExample: {
              problem: 'Find the factors of 12',
              solution: [
                'Pair 1 with 12: §1 × 12§',
                'Pair 2 with 6: §2 × 6§',
                'Pair 3 with 4: §3 × 4§',
                'Factors: 1, 2, 3, 4, 6, 12',
              ],
            },
            greTrap:
              'Do not stop after finding small factors; every small factor usually has a partner.',
            checkQuestion:
              'If §5§ is a factor of §40§, what is its paired factor?',
            example: {
              problem: 'Find all factors of 36',
              solution: [
                'Check factor pairs systematically:',
                '§1 × 36 = 36§ → factors: 1, 36',
                '§2 × 18 = 36§ → factors: 2, 18',
                '§3 × 12 = 36§ → factors: 3, 12',
                '§4 × 9 = 36§ → factors: 4, 9',
                '§6 × 6 = 36§ → factor: 6',
                'Stop at §sqrt(36) = 6§',
                'Answer: 1, 2, 3, 4, 6, 9, 12, 18, 36',
              ],
            },
          },
          {
            title: 'GCD and LCM',
            prerequisiteNote:
              'Review prime factorization before using GCD and LCM shortcuts.',
            vocabulary: ['GCD', 'LCM', 'prime factorization', 'shared factor'],
            explanation:
              'GCD is the greatest common divisor, LCM is the least common multiple. Formula: §GCD(a,b) × LCM(a,b) = a × b§.',
            microExample: {
              problem: 'Find §GCD(12,18)§',
              solution: [
                '§12 = 2^2 × 3§',
                '§18 = 2 × 3^2§',
                'Use shared primes with smaller powers: §2 × 3 = 6§',
              ],
            },
            greTrap:
              'For common divisors, count factors of the GCD, not factors of both original numbers separately.',
            checkQuestion:
              'To count numbers that divide both §a§ and §b§, what number should you factor first?',
            example: {
              problem: 'Find GCD(48, 72) and LCM(48, 72)',
              solution: [
                'Prime factorization:',
                '§48 = 2^4 × 3^1§',
                '§72 = 2^3 × 3^2§',
                'GCD: take minimum powers → §2^3 × 3^1 = 8 × 3 = 24§',
                'LCM: take maximum powers → §2^4 × 3^2 = 16 × 9 = 144§',
                'Check: §24 × 144 = 3456 = 48 × 72§ ✓',
                'Answer: GCD = 24, LCM = 144',
              ],
            },
          },
          {
            title: 'Counting Common Divisors',
            explanation:
              'To count how many positive integers divide both §a§ and §b§, first find §GCD(a,b)§, then count the positive factors of that GCD.',
            example: {
              problem: 'How many positive integers divide both 48 and 150?',
              solution: [
                'Prime factorization:',
                '§48 = 2^4 × 3§',
                '§150 = 2 × 3 × 5^2§',
                'Common prime powers use the smaller exponents, so §GCD(48,150) = 2 × 3 = 6§',
                'The common divisors of 48 and 150 are exactly the positive factors of 6',
                'Factors of 6: 1, 2, 3, 6',
                'Answer: 4',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'divisibility',
      title: 'Divisibility Rules',
      content: {
        introduction:
          'Master these quick divisibility tests to save time on the GRE and solve problems efficiently.',

        concepts: [
          {
            title: 'Common Divisibility Rules',
            explanation:
              'Learn the patterns: divisible by 2 (even last digit), by 3 (sum of digits divisible by 3), by 4 (last two digits divisible by 4), by 5 (ends in 0 or 5), by 9 (sum of digits divisible by 9).',
            example: {
              problem: 'Is 2,847 divisible by 3 and by 9?',
              solution: [
                'Sum of digits: §2 + 8 + 4 + 7 = 21§',
                'Check divisibility by 3: §21 ÷ 3 = 7§ → yes, divisible by 3',
                'Check divisibility by 9: §21 ÷ 9 ≈ 2.33§ → no, not divisible by 9',
                'Answer: Divisible by 3 but not by 9',
              ],
            },
          },
          {
            title: 'Advanced Divisibility Tests',
            explanation:
              'For larger numbers, use compound rules: divisible by 6 (divisible by both 2 and 3), by 8 (last three digits divisible by 8), by 12 (divisible by both 3 and 4).',
            example: {
              problem: 'Which is divisible by 4: 1,234 or 1,236?',
              solution: [
                'Check last two digits only:',
                '1,234: last two digits are 34',
                '§34 ÷ 4 = 8.5§ → not divisible',
                '1,236: last two digits are 36',
                '§36 ÷ 4 = 9§ → divisible',
                'Answer: 1,236 is divisible by 4',
              ],
            },
          },
          {
            title: 'Remainder Patterns and Modular Cycles',
            prerequisiteNote:
              'A remainder is what is left after division by a fixed number.',
            vocabulary: ['remainder', 'modulo', 'cycle', 'divisor'],
            explanation:
              'For GRE remainder questions, look for cycles. Powers, units digits, and repeated additions often repeat their remainders after a small number of steps.',
            microExample: {
              problem: 'Find the pattern of §2^n§ modulo 5.',
              solution: [
                '§2^1 = 2§ leaves remainder 2.',
                '§2^2 = 4§ leaves remainder 4.',
                '§2^3 = 8§ leaves remainder 3.',
                '§2^4 = 16§ leaves remainder 1.',
                'Then the pattern repeats: 2, 4, 3, 1.',
              ],
            },
            greTrap:
              'Do not calculate huge powers directly. Find the cycle length, then reduce the exponent by the cycle.',
            checkQuestion:
              'If a remainder pattern repeats every 4 terms, what exponent matters for §2^27§?',
            example: {
              problem: 'What is the remainder when §2^27§ is divided by 5?',
              solution: [
                'The remainders of §2^n§ modulo 5 repeat: 2, 4, 3, 1.',
                'The cycle length is 4.',
                '§27 ÷ 4§ leaves remainder 3, so use the 3rd item in the cycle.',
                'The 3rd remainder is 3.',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 80,
      question: 'How many integers are there between -5 and 5, inclusive?',
      options: ['9', '10', '11', '12'],
      correct: 2,
      explanation:
        "Count: -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5. That's 11 integers.",
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If n is an even integer, which of the following must be odd?',
      options: ['n + 2', '2n', 'n²', 'n + 1'],
      correct: 3,
      explanation:
        'If n is even, then n + 1 is odd. The others: n + 2 is even, 2n is even, §n²§ is even.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'What is the smallest prime number greater than 50?',
      options: ['51', '52', '53', '55'],
      correct: 2,
      explanation:
        '51 = 3 × 17, 52 = 4 × 13, 53 is prime (check: not divisible by 2, 3, 5, 7), 55 = 5 × 11.',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'If the product of two integers is odd, which must be true?',
      options: [
        'Both are odd',
        'Both are even',
        'One is odd, one is even',
        'At least one is prime',
      ],
      correct: 0,
      explanation:
        'For a product to be odd, both factors must be odd. If either factor is even, the product is even.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'What is the GCD of 48 and 72?',
      options: ['12', '24', '36', '8'],
      correct: 1,
      explanation:
        '§48 = 2^4 × 3§, §72 = 2^3 × 3^2§. GCD = §2^3 × 3 = 8 × 3 = 24§.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question: 'How many positive integers divide both 48 and 150?',
      options: ['4', '6', '8', '10'],
      correct: 0,
      explanation:
        'Find the GCD first: §48 = 2^4 × 3§ and §150 = 2 × 3 × 5^2§, so §GCD(48,150)=2×3=6§. The positive factors of 6 are 1, 2, 3, and 6, so there are 4 common divisors.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'If n is divisible by both 6 and 8, what is the smallest possible positive value of n?',
      options: ['12', '24', '48', '16', '36'],
      correct: 1,
      explanation:
        'Find LCM of 6 and 8. §6 = 2 × 3§, §8 = 2^3§. LCM = §2^3 × 3 = 24§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What is the greatest common divisor (GCD) of 48 and 72?',
      options: ['24', '12', '8', '6', '16'],
      correct: 0,
      explanation:
        '§48 = 2^4 × 3§, §72 = 2^3 × 3^2§. GCD = §2^3 × 3 = 8 × 3 = 24§.',
    },
    {
      type: 'untimed',
      question: 'How many factors does 60 have?',
      options: ['10', '12', '14', '16'],
      correct: 1,
      explanation:
        '§60 = 2^2 × 3 × 5§. Number of factors = §(2+1)(1+1)(1+1) = 12§. They are: 1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60.',
    },
    {
      type: 'untimed',
      question: 'Which number is divisible by both 4 and 9?',
      options: ['144', '126', '132', '138'],
      correct: 0,
      explanation:
        '144: divisible by 4 (§44÷4=11§) and by 9 (§1+4+4=9§). Others fail one test: 126 not by 4, 132 not by 9, 138 not by either.',
    },
    {
      type: 'untimed',
      question: 'What is the units digit of §7^{15}§?',
      options: ['1', '3', '7', '9'],
      correct: 1,
      explanation:
        'Powers of 7: §7^1=7§, §7^2=49§ (9), §7^3=343§ (3), §7^4=2401§ (1), §7^5=16807§ (7). Pattern repeats every 4. §15 = 4×3 + 3§, so §7^{15}§ has same units digit as §7^3§, which is 3.',
    },
    {
      type: 'untimed',
      question:
        'If §a§ and §b§ are consecutive even integers with §a < b§, what is §b - a§?',
      options: ['1', '2', '3', '4'],
      correct: 1,
      explanation:
        'Consecutive even integers differ by 2. For example: 4 and 6, or 10 and 12. So §b - a = 2§.',
    },
    {
      type: 'untimed',
      question:
        'The sum of how many consecutive integers starting from 1 equals 55?',
      options: ['9', '10', '11', '12'],
      correct: 1,
      explanation:
        'Sum of first §n§ integers = §n(n+1)/2§. Need §n(n+1)/2 = 55§, so §n(n+1) = 110§. Testing: §10×11 = 110§. So §n = 10§.',
    },
    {
      type: 'untimed',
      question:
        'If §m§ is an odd integer, which of the following must be even?',
      options: ['§m + 1§', '§m^2§', '§m + 2§', '§2m + 1§', '§3m§'],
      correct: 0,
      explanation:
        'Odd + 1 = Even. For example: §5 + 1 = 6§. Options B (odd²=odd), C (odd+2=odd), D (2×odd+1=odd), and E (3×odd=odd) are all odd.',
    },
    {
      type: 'untimed',
      question: 'What is the product of §(-4) × (-7) × 2 × (-1)§?',
      options: ['§-56§', '§56§', '§-28§', '§28§', '§14§'],
      correct: 0,
      explanation:
        'Count negatives: 3 negative signs. Odd number of negatives = negative result. §4 × 7 × 2 × 1 = 56§, so answer is §-56§.',
    },
    {
      type: 'untimed',
      question:
        'If x and y are consecutive integers with x < y, and their sum is 27, what is y?',
      options: ['14', '13', '15', '12', '16'],
      correct: 0,
      explanation:
        '§x + y = 27§. Since consecutive: §y = x + 1§. So §x + (x + 1) = 27§ → §2x + 1 = 27§ → §2x = 26§ → §x = 13§, §y = 14§.',
    },

    {
      type: 'timed',
      question: 'How many factors does 60 have?',
      options: ['10', '12', '14', '16'],
      correct: 1,
      explanation:
        '§60 = 2^2 × 3 × 5§. Number of factors = §(2+1)(1+1)(1+1) = 12§. They are: 1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60.',
    },
    {
      type: 'timed',
      question: 'Which number is divisible by both 4 and 9?',
      options: ['144', '126', '132', '138'],
      correct: 0,
      explanation:
        '144: divisible by 4 (§44÷4=11§) and by 9 (§1+4+4=9§). Others fail one test: 126 not by 4, 132 not by 9, 138 not by either.',
    },
    {
      type: 'timed',
      question: 'What is the units digit of §7^{15}§?',
      options: ['1', '3', '7', '9'],
      correct: 1,
      explanation:
        'Powers of 7: §7^1=7§, §7^2=49§ (9), §7^3=343§ (3), §7^4=2401§ (1), §7^5=16807§ (7). Pattern repeats every 4. §15 = 4×3 + 3§, so §7^{15}§ has same units digit as §7^3§, which is 3.',
    },
    {
      type: 'timed',
      question:
        'If §a§ and §b§ are consecutive even integers with §a < b§, what is §b - a§?',
      options: ['1', '2', '3', '4'],
      correct: 1,
      explanation:
        'Consecutive even integers differ by 2. For example: 4 and 6, or 10 and 12. So §b - a = 2§.',
    },
    {
      type: 'timed',
      question:
        'The sum of how many consecutive integers starting from 1 equals 55?',
      options: ['9', '10', '11', '12'],
      correct: 1,
      explanation:
        'Sum of first §n§ integers = §n(n+1)/2§. Need §n(n+1)/2 = 55§, so §n(n+1) = 110§. Testing: §10×11 = 110§. So §n = 10§.',
    },
    {
      type: 'timed',
      question:
        'If §m§ is an odd integer, which of the following must be even?',
      options: ['§m + 1§', '§m^2§', '§m + 2§', '§2m + 1§', '§3m§'],
      correct: 0,
      explanation:
        'Odd + 1 = Even. For example: §5 + 1 = 6§. Options B (odd²=odd), C (odd+2=odd), D (2×odd+1=odd), and E (3×odd=odd) are all odd.',
    },
    {
      type: 'timed',
      question: 'What is the product of §(-4) × (-7) × 2 × (-1)§?',
      options: ['§-56§', '§56§', '§-28§', '§28§', '§14§'],
      correct: 0,
      explanation:
        'Count negatives: 3 negative signs. Odd number of negatives = negative result. §4 × 7 × 2 × 1 = 56§, so answer is §-56§.',
    },
    {
      type: 'timed',
      question:
        'If x and y are consecutive integers with x < y, and their sum is 27, what is y?',
      options: ['14', '13', '15', '12', '16'],
      correct: 0,
      explanation:
        '§x + y = 27§. Since consecutive: §y = x + 1§. So §x + (x + 1) = 27§ → §2x + 1 = 27§ → §2x = 26§ → §x = 13§, §y = 14§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'If §n§ is an odd integer, compare the two quantities.',
      quantityA: 'The parity of §n + 1§',
      quantityB: 'Even',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which expressions must be even when §k§ is an integer? Select all that apply.',
      options: ['§2k§', '§k^2 + k§', '§2k + 1§', '§4k - 6§', '§k + 2§'],
      correct: [0, 1, 3],
      explanation:
        '§2k§ and §4k - 6§ are even. Also §k^2 + k = k(k+1)§ is a product of consecutive integers, so it is even.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'How many positive factors does §36§ have?',
      correctAnswer: '9',
      acceptedAnswers: ['9'],
      explanation:
        '§36 = 2^2 * 3^2§, so the number of positive factors is §(2+1)(2+1)=9§.',
    },
  ];

  const quickReference = [
    'Even: divisible by 2: (§2k§)',
    'Odd: not divisible by 2: (§2k+1§)',
    'Prime: exactly 2 factors: (1 and itself)',
    'Composite: more than 2 factors',
    '§GCD(a,b) × LCM(a,b) = a × b§',
    'Common divisors of §a§ and §b§ = positive factors of §GCD(a,b)§',
    'Remainder patterns: find the cycle length, then reduce the exponent or position',
    'Sum of first §n§ integers: §n(n+1)/2§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Integer concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Integer concepts for the GRE Quantitative section',
    aicontext:
      'Master even/odd numbers, prime/composite numbers, factors, multiples, GCD, and LCM',
  };

  return (
    <LessonTemplate
      lessonId="integers"
      lessonTitle="Integers"
      lessonIcon={<FiHash />}
      lessonDescription="Master the fundamental properties of integers, including prime numbers, factors, and divisibility"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="integers" />}
    />
  );
};

export default IntegersLesson;
